/**
 * Approval: where the template stands with Meta, and what to do about it.
 *
 * Four states, one panel. A **draft** has never been to Meta and can be
 * submitted. A **pending** one is with Meta and there is nothing to press —
 * Meta's webhook moves it, which is said plainly so that nobody goes looking for
 * a refresh button that would not help. An **approved** one is available to a
 * campaign. A **rejected** one carries Meta's reason verbatim, the repair
 * `@wend/shared` proposes for it, and the only route back.
 *
 * **THE RESUBMIT IS AN EXPLICIT HUMAN ACTION, AND THE GATE IS REAL.** Nothing in
 * Wend resubmits on its own: a rejection answered automatically is how a
 * WhatsApp Business Account gets flagged. `waResubmitTemplateRequestSchema`
 * requires `acknowledgedReason` — a person restating what Meta refused, in their
 * own words — and the corrected document travels with it. So this screen asks
 * for that sentence and will not enable the button without it. It is not a
 * confirmation dialog, which is a thing people learn to click through; it is a
 * sentence somebody has to have read the rejection to write.
 *
 * **Meta's words are printed unchanged.** The heading above them says which of
 * the seven reason codes it is; the paragraph is Meta's own. Rewriting Meta's
 * sentence would give a person two accounts of one refusal that can disagree,
 * and only one of them is the reason the template was actually rejected.
 *
 * ## Slice 17 — three sentences that were kept, and where the words went
 *
 * The four state callouts were four paragraphs and are four clauses. What is
 * still here in full, because a reader would act wrongly without it:
 *
 *   - **there is nothing to press while Meta holds it.** Moved into the title,
 *     which is the change that matters: it was the last sentence of a paragraph,
 *     and the person it is for is the one already scanning for a refresh button.
 *     They find Submit instead, and resubmitting into an open review is how a
 *     WhatsApp Business Account gets flagged;
 *   - **submitting sends the *stored* template.** Also promoted into a title. A
 *     person with unsaved edits would otherwise send Meta a document they are
 *     not looking at;
 *   - **"Sends no message to anybody."** This is a button labelled Submit, on a
 *     screen about a marketing message, in a product whose database holds 752
 *     real phone numbers. It is the sentence that tells a hesitating person it is
 *     safe to press, and it is not shortened further.
 *
 * `WA_RESUBMIT_IS_MANUAL` is untouched and still printed verbatim above the
 * acknowledgement box: it is the contract's own wording for why a human has to
 * write that sentence, and a second account of it here would drift.
 */
import {
  waResubmitTemplateRequestSchema,
  type Template,
  type WaTemplateView,
} from '@wend/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  useToast,
} from '@wend/ui';
import { Send } from 'lucide-react';
import { useState } from 'react';

import { FormAlert } from '../../../components/FormAlert';
import { TEMPLATE_STATUS_TONE } from '../../../components/TemplateStatusBadge';
import { Textarea } from '../../../components/Textarea';
import { errorMessage, isApiError } from '../../../lib/api/errors';
import { resubmitWaTemplate, submitWaTemplate } from '../../../lib/api/whatsapp';
import { formatDateTime } from '../../../lib/format';
import {
  META_REJECTION_REASON_TITLE,
  WA_PROPOSED_FIX_TITLE,
  WA_RESUBMIT_IS_MANUAL,
  WA_STATUS_DISPLAY,
} from '../../../lib/whatsapp-display';
import { waDocumentOf, type WaDraft } from './wa-draft';

export interface WaApprovalPanelProps {
  template: Template;
  whatsapp: WaTemplateView;
  draft: WaDraft;
  /** True while the composer holds edits the API has not seen. */
  dirty: boolean;
  /** Re-reads the template after Meta's answer changes it. */
  onChanged: () => void;
}

export function WaApprovalPanel({
  template,
  whatsapp,
  draft,
  dirty,
  onChanged,
}: WaApprovalPanelProps) {
  const status = WA_STATUS_DISPLAY[template.status];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <Badge tone={TEMPLATE_STATUS_TONE[template.status]}>{status.label}</Badge>
          {whatsapp.metaTemplateId !== null && (
            <span className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
              meta id {whatsapp.metaTemplateId}
            </span>
          )}
          {whatsapp.metaStatus !== null && (
            <span className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
              meta says {whatsapp.metaStatus}
            </span>
          )}
        </div>
        <CardTitle className="text-[1rem]">Approval</CardTitle>
        <CardDescription>{status.meaning}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {template.status === 'draft' && (
          <SubmitPanel
            template={template}
            whatsapp={whatsapp}
            dirty={dirty}
            onChanged={onChanged}
          />
        )}

        {/*
          "There is nothing to press" is the load-bearing half: without it a
          person hunts for a refresh, finds Submit, and resubmits into an open
          review — which is how a WhatsApp Business Account gets flagged. The
          timing expectation stays with it because "nothing to press" alone reads
          as a screen that has given up.
        */}
        {template.status === 'pending' && (
          <Callout tone="info" title="Waiting on Meta — there is nothing to press">
            Usually minutes, occasionally a day. Wend updates this when Meta’s webhook arrives.
            Resubmitting while a review is open is how an account gets flagged.
          </Callout>
        )}

        {template.status === 'approved' && (
          <Callout tone="good" title="Approved">
            A WhatsApp campaign can use this template. Editing it means submitting it to Meta
            again.
          </Callout>
        )}

        {whatsapp.rejection !== null && (
          <RejectionPanel
            template={template}
            rejection={whatsapp.rejection}
            draft={draft}
            onChanged={onChanged}
          />
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Submit                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The first submission.
 *
 * Two things stop it, and both are said rather than left as a disabled button
 * with no explanation. **Unsaved edits**: `POST /submit` sends no body, so what
 * goes to Meta is what is stored — submitting over unsaved changes would send a
 * document nobody has seen. **`submittable: false`**: the API has already run
 * `collectWaTemplateVariableIssues` over the stored document and its answer is
 * on the template; the composer shows the same list above.
 */
function SubmitPanel({
  template,
  whatsapp,
  dirty,
  onChanged,
}: {
  template: Template;
  whatsapp: WaTemplateView;
  dirty: boolean;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{ message: string; requestId?: string } | null>(null);

  async function onSubmit(): Promise<void> {
    if (pending) return;
    setFailure(null);
    setPending(true);
    try {
      const response = await submitWaTemplate(template.id);
      toast({
        tone: 'success',
        title: 'Submitted to Meta',
        description: `Meta has it as of ${formatDateTime(response.submission.submittedAt)}. Nothing has been sent to anybody.`,
      });
      onChanged();
    } catch (error) {
      setFailure({
        message: errorMessage(error),
        ...(isApiError(error) && error.requestId !== undefined
          ? { requestId: error.requestId }
          : {}),
      });
    }
    setPending(false);
  }

  const blocked = dirty || !whatsapp.submittable;

  return (
    <div className="flex flex-col gap-4">
      {failure !== null && (
        <FormAlert
          tone="error"
          {...(failure.requestId === undefined ? {} : { requestId: failure.requestId })}
        >
          {failure.message}
        </FormAlert>
      )}

      {/*
        Two reasons the button is disabled, each said rather than left to be
        guessed. The first is kind 2: submitting sends the *stored* document, so
        somebody who has edited and not saved would otherwise send Meta a version
        they are not looking at.
      */}
      {dirty && (
        <Callout tone="warn" title="Save first — submitting sends the stored template">
          What is on screen has not been saved, so it would not be what Meta reviews.
        </Callout>
      )}

      {!dirty && !whatsapp.submittable && (
        <Callout tone="bad" title="Meta would refuse this">
          Fix the problems listed above first.
        </Callout>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          size="sm"
          loading={pending}
          disabled={blocked}
          onClick={() => {
            void onSubmit();
          }}
        >
          <Send aria-hidden="true" className="size-4" />
          {pending ? 'Sending to Meta' : 'Submit for approval'}
        </Button>
        {/*
          "It sends no message to anybody" survives every cut. This is a button
          labelled Submit on a screen about a marketing message, in a product
          whose database holds 752 real phone numbers — the one sentence that
          tells a hesitating person it is safe to press.
        */}
        <p className="text-[0.85rem] text-muted">
          Creates the template in your own WhatsApp Business Account. Sends no message to anybody.
        </p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Rejection, and the way back                                                 */
/* -------------------------------------------------------------------------- */

function RejectionPanel({
  template,
  rejection,
  draft,
  onChanged,
}: {
  template: Template;
  rejection: NonNullable<WaTemplateView['rejection']>;
  draft: WaDraft;
  onChanged: () => void;
}) {
  const { toast } = useToast();
  const [acknowledged, setAcknowledged] = useState('');
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<{ message: string; requestId?: string } | null>(null);

  const reasonTitle =
    rejection.reasonCode === null
      ? 'Meta gave a reason Wend does not recognise'
      : META_REJECTION_REASON_TITLE[rejection.reasonCode];

  async function onResubmit(): Promise<void> {
    if (pending) return;
    setFailure(null);

    const request = waResubmitTemplateRequestSchema.safeParse({
      acknowledgedReason: acknowledged,
      document: waDocumentOf(draft),
    });
    if (!request.success) {
      setFailure({
        message:
          request.error.issues[0]?.message ??
          'Write what Meta refused, and fix the template, before resubmitting.',
      });
      return;
    }

    setPending(true);
    try {
      const response = await resubmitWaTemplate(template.id, request.data);
      toast({
        tone: 'success',
        title: 'Resubmitted to Meta',
        description: `Meta has the corrected template as of ${formatDateTime(response.submission.submittedAt)}.`,
      });
      setAcknowledged('');
      onChanged();
    } catch (error) {
      setFailure({
        message: errorMessage(error),
        ...(isApiError(error) && error.requestId !== undefined
          ? { requestId: error.requestId }
          : {}),
      });
    }
    setPending(false);
  }

  return (
    <div className="flex flex-col gap-5">
      <Callout tone="bad" title={reasonTitle}>
        {/* Meta's own sentence, unchanged. */}
        <p>{rejection.reason}</p>
        <p className="mt-2 font-mono text-[0.72rem] uppercase tracking-[0.1em] text-muted">
          rejected {formatDateTime(rejection.rejectedAt)}
          {rejection.reasonCode !== null && <> · code {rejection.reasonCode}</>}
        </p>
      </Callout>

      <Callout tone="info" title={WA_PROPOSED_FIX_TITLE[rejection.proposedFix.kind]}>
        <p>{rejection.proposedFix.summary}</p>
        {rejection.proposedFix.category !== null && (
          <p className="mt-2">
            Set the category to{' '}
            <span className="text-text">{rejection.proposedFix.category}</span> above, check the
            content really belongs there, and resubmit.
          </p>
        )}
      </Callout>

      <div className="flex flex-col gap-3 rounded-base border border-line bg-white/[0.02] p-4">
        <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">
          {WA_RESUBMIT_IS_MANUAL}
        </p>

        {failure !== null && (
          <FormAlert
            tone="error"
            {...(failure.requestId === undefined ? {} : { requestId: failure.requestId })}
          >
            {failure.message}
          </FormAlert>
        )}

        <label className="flex flex-col gap-2">
          <span className="text-[0.9rem] text-text">
            In your own words, what did Meta refuse and what have you changed?
          </span>
          <Textarea
            rows={3}
            value={acknowledged}
            placeholder="Meta recategorised this as marketing; I have moved it to Marketing and removed the discount line from the header."
            onChange={(event) => {
              setAcknowledged(event.target.value);
            }}
          />
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            size="sm"
            loading={pending}
            disabled={acknowledged.trim() === ''}
            onClick={() => {
              void onResubmit();
            }}
          >
            <Send aria-hidden="true" className="size-4" />
            {pending ? 'Sending to Meta' : 'Resubmit to Meta'}
          </Button>
          <p className="text-[0.85rem] text-muted">
            Sends the template as it stands on this screen, together with what you wrote above.
          </p>
        </div>
      </div>
    </div>
  );
}
